// Shared chrome for the public auth surfaces — Login, ResetPassword,
// AcceptInvitationPage. Centralises the BeneCRM brand lockup, the
// canvas treatment (Parchment + a barely-there teal-tint radial wash),
// and the elevated raised card so each page stays inside the v2
// design vocabulary.
//
// Two layout modes:
//   • ``centered`` (default) — single column, logo above a max-w-sm
//     card. Best for Login + ResetPassword.
//   • ``split``               — two-column on md+, logo + supporting
//     copy on the left, form column on the right. Best for the
//     Accept-Invitation "ok" preview where context matters.
//
// Why ``shadow-overlay`` instead of the at-rest "no shadow" rule? The
// auth surfaces are full-screen modal-like landings (no surrounding
// chrome), so the same elevation tier the Modal uses is the right
// match — the card needs to read as the focal element.

import type { ReactNode } from "react";

import { cn } from "@/design-system/components/cn";


export interface AuthShellProps {
  children: ReactNode;
  layout?: "centered" | "split";
  /**
   * Optional content rendered on the left rail of the ``split``
   * layout — typically a tagline + supporting context. Ignored in
   * ``centered`` mode.
   */
  side?: ReactNode;
  /**
   * data-testid for the outer wrapper. Defaults to ``auth-shell``.
   */
  testId?: string;
}


/**
 * Soft radial wash + faint grid framing the canvas. Stays inside
 * tokens (teal-tint, border-subtle) and only appears at low opacity
 * so type contrast on the card is unaffected.
 */
function CanvasTexture() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      data-testid="auth-shell-texture"
    >
      {/* Top-left teal-tint radial wash — pulls the eye to the brand
          mark without adding chroma to the card itself. */}
      <div
        className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-teal-tint opacity-50 blur-3xl"
      />
      {/* Bottom-right sage-tint counter-wash for visual balance. */}
      <div
        className="absolute -bottom-40 -right-32 h-[480px] w-[480px] rounded-full bg-sage-tint opacity-40 blur-3xl"
      />
    </div>
  );
}


function BrandLockup({
  variant = "horizontal",
}: { variant?: "horizontal" | "symbol" }) {
  if (variant === "symbol") {
    return (
      <img
        src="/brand/benecrm-logo-symbol.png"
        alt="BeneCRM"
        className="h-12 w-12"
      />
    );
  }
  return (
    <img
      src="/brand/benecrm-logo.png"
      alt="BeneCRM"
      className="h-9 w-auto"
    />
  );
}


export function AuthShell({
  children, layout = "centered", side, testId = "auth-shell",
}: AuthShellProps) {
  return (
    <div
      data-testid={testId}
      data-layout={layout}
      className="relative flex min-h-screen items-center justify-center bg-canvas px-4 py-10"
    >
      <CanvasTexture />

      {layout === "centered" ? (
        <div className="relative z-10 flex w-full max-w-sm flex-col items-center gap-6">
          <BrandLockup />
          <div
            className={cn(
              "w-full rounded-lg border border-border-subtle bg-raised p-8 shadow-overlay",
            )}
          >
            {children}
          </div>
          <p className="text-xs text-text-tertiary">
            Compliance workflow for benefits brokers.
          </p>
        </div>
      ) : (
        <div className="relative z-10 grid w-full max-w-3xl overflow-hidden rounded-lg border border-border-subtle bg-raised shadow-overlay md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          {/* Left rail — symbol + tagline + page-supplied context.
              Hidden on small screens so the form gets the full
              width on mobile. */}
          <aside
            data-testid="auth-shell-side"
            className="hidden flex-col justify-between gap-6 border-r border-border-subtle bg-recessed p-8 md:flex"
          >
            <BrandLockup variant="horizontal" />
            <div className="flex flex-col gap-3">
              {side}
            </div>
            <p className="text-xs text-text-tertiary">
              Compliance workflow for benefits brokers.
            </p>
          </aside>
          <div className="flex flex-col gap-2 p-8">
            {/* On mobile we still need the brand mark — the side rail
                is hidden, so render the lockup inline above the form. */}
            <div className="mb-2 flex md:hidden">
              <BrandLockup />
            </div>
            {children}
          </div>
        </div>
      )}
    </div>
  );
}
